import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  MdSearch,
  MdCheckCircle,
  MdReceipt,
  MdTrendingUp,
  MdGavel,
  MdShoppingCart,
} from 'react-icons/md';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ClearFiltersButton, PageSkeleton } from '@euroasiann/shared/ui';
import { authenticatedFetch } from '../../lib/api';
import { cn } from '../../lib/utils';
import type { PipelineKind } from '../../hooks/usePipelineOverviewCounts';
import { Pagination } from '../../components/shared/Pagination';

const chartTooltipStyle: CSSProperties = {
  background: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: 10,
  color: 'hsl(var(--foreground))',
  boxShadow: '0 8px 24px rgba(15, 23, 42, 0.16)',
  fontSize: 12,
  fontWeight: 600,
  padding: '8px 12px',
};

/** Vertical guide only — no solid white hover rectangle. */
function VerticalGuideCursor({
  x,
  y,
  width,
  height,
  top,
}: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  top?: number;
}) {
  if (typeof x !== 'number' || Number.isNaN(x)) return null;
  const lineTop = typeof top === 'number' ? top : y ?? 0;
  const lineHeight = Number(height) || 0;
  if (lineHeight <= 0) return null;
  const xPos = x + (Number(width) || 0) / 2;
  return (
    <line
      x1={xPos}
      y1={lineTop}
      x2={xPos}
      y2={lineTop + lineHeight}
      stroke="#0088cc"
      strokeWidth={2}
      strokeLinecap="round"
      pointerEvents="none"
    />
  );
}

type CompletedOrderRow = {
  _id: string;
  poNumber: string;
  title: string;
  status: string;
  totalAmount: number;
  currency: string;
  closedAt: string;
  organizationName: string;
  vendorOrganizationName: string;
  rfqNumber?: string;
  claimsSummary: {
    total: number;
    accepted: number;
    rejected: number;
    pending: number;
  };
};

type ArchivePayload = {
  summary: {
    orderCount: number;
    totalValue: number;
    averageOrderValue: number;
    ordersWithClaims: number;
    ordersWithoutClaims: number;
    claimsAccepted: number;
    claimsRejected: number;
    primaryCurrency: string;
    valueByCurrency?: Record<string, number>;
  };
  monthlyCompletions: Array<{ month: string; count: number; value: number }>;
  orders: CompletedOrderRow[];
};

function formatMoney(amount: number, currency: string) {
  return `${currency} ${amount.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function CompletedOrdersPage({
  pipeline,
  embedded = false,
}: {
  pipeline: PipelineKind;
  embedded?: boolean;
}) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-completed-orders', pipeline],
    queryFn: async () => {
      const res = await authenticatedFetch(
        `/api/v1/admin/completed-orders?pipeline=${pipeline}`
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to load order history');
      }
      const json = await res.json();
      return json.data as ArchivePayload;
    },
  });

  const filtered = useMemo(() => {
    const list = data?.orders || [];
    if (!searchQuery.trim()) return list;
    const q = searchQuery.toLowerCase();
    return list.filter(
      (o) =>
        o.poNumber.toLowerCase().includes(q) ||
        o.title.toLowerCase().includes(q) ||
        o.organizationName.toLowerCase().includes(q) ||
        o.vendorOrganizationName.toLowerCase().includes(q) ||
        (o.rfqNumber || '').toLowerCase().includes(q)
    );
  }, [data?.orders, searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);
  
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, currentPage]);

  const subtitle =
    pipeline === 'sales'
      ? 'Closed sales POs with all delivery claims settled — historical performance and totals.'
      : 'Closed purchase POs with all delivery claims settled — vendor fulfilment archive.';

  if (isLoading) {
    return <PageSkeleton variant="table" />;
  }

  if (error || !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800 text-sm">
        {(error as Error)?.message || 'Failed to load order history'}
      </div>
    );
  }

  const { summary, monthlyCompletions } = data;
  const currency = summary.primaryCurrency || 'USD';

  const statCards = [
    {
      label: 'Orders in history',
      value: summary.orderCount.toLocaleString(),
      icon: MdCheckCircle,
      valueClass: 'text-emerald-600',
      boxClass: 'bg-emerald-50 border-emerald-100',
    },
    {
      label: 'Total value',
      value: formatMoney(summary.totalValue, currency),
      icon: MdTrendingUp,
      valueClass: 'text-[#0088cc]',
      boxClass: 'bg-[#0088cc]/10 border-[#0088cc]/20',
    },
    {
      label: 'Average order',
      value: formatMoney(summary.averageOrderValue, currency),
      icon: MdReceipt,
      valueClass: 'text-indigo-600',
      boxClass: 'bg-indigo-50 border-indigo-100',
    },
    {
      label: 'Claims settled',
      value: `${summary.claimsAccepted + summary.claimsRejected} (${summary.claimsAccepted} accepted · ${summary.claimsRejected} rejected)`,
      icon: MdGavel,
      valueClass: 'text-amber-700',
      boxClass: 'bg-amber-50 border-amber-100',
    },
  ];

  return (
    <div
      className={cn(
        'w-full min-w-0 space-y-6 pb-8',
        embedded ? 'mb-6' : 'min-h-screen p-4 lg:p-8 bg-[hsl(var(--background))]'
      )}
    >
      {embedded && (
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{subtitle}</p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 mb-4">
        {statCards.map((card) => {
          const Icon = card.icon;
          return (
            <div
              key={card.label}
              className={cn('rounded-xl border p-4 shadow-sm', card.boxClass)}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  {card.label}
                </p>
                <Icon className={cn('h-5 w-5 shrink-0', card.valueClass)} />
              </div>
              <p className={cn('mt-2 text-lg font-black tabular-nums', card.valueClass)}>
                {card.value}
              </p>
            </div>
          );
        })}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 sm:p-6 shadow-sm">
          <h3 className="text-sm font-bold text-[hsl(var(--foreground))] mb-1">
            Completions by month
          </h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">
            Orders closed in the last 6 months
          </p>
          <div className="h-56 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthlyCompletions} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-[hsl(var(--border))]" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip
                  cursor={<VerticalGuideCursor />}
                  contentStyle={chartTooltipStyle}
                  labelStyle={{ color: 'hsl(var(--foreground))', fontWeight: 700 }}
                  itemStyle={{ color: '#0088cc' }}
                  formatter={(value: number, name: string) =>
                    name === 'count' ? [value, 'Orders'] : [formatMoney(value, currency), 'Value']
                  }
                />
                <Bar dataKey="count" fill="#0088cc" name="count" radius={[4, 4, 0, 0]} maxBarSize={48} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 sm:p-6 shadow-sm space-y-3">
          <h3 className="text-sm font-bold text-[hsl(var(--foreground))]">Claim footprint</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[hsl(var(--muted-foreground))]">Orders with claims</span>
              <span className="font-bold">{summary.ordersWithClaims}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-[hsl(var(--muted-foreground))]">Orders without claims</span>
              <span className="font-bold">{summary.ordersWithoutClaims}</span>
            </div>
          </div>
          {summary.valueByCurrency && Object.keys(summary.valueByCurrency).length > 1 && (
            <div className="pt-3 border-t border-[hsl(var(--border))]">
              <p className="text-[10px] font-bold uppercase tracking-wider text-[hsl(var(--muted-foreground))] mb-2">
                Value by currency
              </p>
              <ul className="space-y-1 text-xs">
                {Object.entries(summary.valueByCurrency).map(([cur, val]) => (
                  <li key={cur} className="flex justify-between">
                    <span>{cur}</span>
                    <span className="font-semibold">{formatMoney(val, cur)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] shadow-sm overflow-hidden mb-10">
        <div className="p-4 sm:p-5 border-b border-[hsl(var(--border))] flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <MdShoppingCart className="h-5 w-5 text-[#0088cc]" />
            <h3 className="font-bold text-[hsl(var(--foreground))]">Order history</h3>
          </div>
          <div className="relative w-full sm:max-w-md">
            <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0088cc]/65 w-5 h-5" />
            <input
              type="text"
              placeholder="Search PO, RFQ, customer, vendor…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-[#0088cc]/20 rounded-xl bg-[hsl(var(--background))] text-sm"
            />
          </div>
          <ClearFiltersButton
            visible={!!searchQuery.trim()}
            onClick={() => setSearchQuery('')}
            variant="toolbar"
          />
        </div>

        {filtered.length === 0 ? (
          <p className="p-8 text-center text-sm text-[hsl(var(--muted-foreground))]">
            No orders in history yet. Orders appear here after the PO is closed and any delivery
            claims are accepted or rejected.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[hsl(var(--muted))]/40 text-left text-[10px] uppercase tracking-wider text-[hsl(var(--muted-foreground))]">
                  <th className="px-4 py-3 font-bold">PO</th>
                  <th className="px-4 py-3 font-bold">Customer</th>
                  <th className="px-4 py-3 font-bold">Vendor</th>
                  <th className="px-4 py-3 font-bold">Amount</th>
                  <th className="px-4 py-3 font-bold">Closed</th>
                  <th className="px-4 py-3 font-bold">Claims</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((row) => (
                  <tr
                    key={row._id}
                    onClick={() => navigate(`/dashboard/admin/pos/${row._id}`)}
                    className="border-t border-[hsl(var(--border))] hover:bg-[#0088cc]/5 cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <p className="font-bold text-[hsl(var(--foreground))]">{row.poNumber}</p>
                      {row.rfqNumber && (
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">
                          RFQ {row.rfqNumber}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3">{row.organizationName}</td>
                    <td className="px-4 py-3">{row.vendorOrganizationName}</td>
                    <td className="px-4 py-3 font-semibold tabular-nums">
                      {formatMoney(row.totalAmount, row.currency)}
                    </td>
                    <td className="px-4 py-3 text-[hsl(var(--muted-foreground))]">
                      {formatDate(row.closedAt)}
                    </td>
                    <td className="px-4 py-3">
                      {row.claimsSummary.total === 0 ? (
                        <span className="text-xs text-[hsl(var(--muted-foreground))]">None</span>
                      ) : (
                        <span className="text-xs font-medium">
                          {row.claimsSummary.accepted} ok · {row.claimsSummary.rejected} rejected
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              currentPage = {currentPage}
              totalPages = {totalPages}
              onPageChange = {setPage}
              itemsPerPage = {PAGE_SIZE}
              totalItems = {filtered.length}
            />
          </div>
        )}
      </div>
    </div>
  );
}
